import Dexie, { type EntityTable } from 'dexie';
import type { WorkOrder, Mold } from '@/types/maintenance';

// ─── Sync queue ───────────────────────────────────────────────────────────────

export type SyncOpType =
  | 'wo:create'
  | 'wo:start'
  | 'wo:block'
  | 'wo:resume'
  | 'wo:close'
  | 'wo:attach_photo';

export type SyncOpStatus = 'pending' | 'syncing' | 'synced' | 'failed';

export interface SyncOperation {
  id: string;                        // crypto.randomUUID()
  type: SyncOpType;
  entity_id: string;                 // WO UUID (for conflict check)
  payload: Record<string, unknown>;  // operation-specific data
  org_id: string;
  user_id: string;
  optimistic_updated_at: string;     // ISO — when the local change was made
  created_at: string;                // ISO — for FIFO ordering
  status: SyncOpStatus;
  retry_count: number;
  error?: string;
}

// ─── Photo staging ────────────────────────────────────────────────────────────

export interface PhotoStaging {
  id: string;
  wo_id: string;
  blob: Blob;            // stored as native Blob — no base64 overhead
  filename: string;
  mime_type: string;
  size: number;
  staged_at: string;
  upload_status: 'pending' | 'uploading' | 'uploaded' | 'failed';
  remote_url?: string;   // filled after successful upload
}

// ─── Database ─────────────────────────────────────────────────────────────────

export class MoldCareDB extends Dexie {
  work_orders!: EntityTable<WorkOrder, 'id'>;
  molds!: EntityTable<Mold, 'id'>;
  sync_queue!: EntityTable<SyncOperation, 'id'>;
  photo_stage!: EntityTable<PhotoStaging, 'id'>;
  cache_meta!: EntityTable<{ key: string; value: string }, 'key'>;

  constructor() {
    super('MoldCareHub');
    this.version(1).stores({
      // Only indexed columns listed here; all other fields are stored automatically
      work_orders: 'id, organization_id, mold_id, assigned_to, status, updated_at, [organization_id+status]',
      molds:       'id, organization_id, status',
      sync_queue:  'id, org_id, entity_id, status, created_at, [org_id+status]',
      photo_stage: 'id, wo_id, upload_status',
      cache_meta:  'key',
    });
  }
}

export const db = new MoldCareDB();

/**
 * Wipes every local Dexie table. Call this on sign-out, sign-in (session
 * transition), and on org switch so one user's cached data never crosses
 * into another user's session.
 *
 * Why: Dexie-cached work_orders / sync_queue / photo_stage are scoped to
 * org_id at write time but reads cannot be trusted to always filter. A
 * full wipe at every auth transition is the simplest correctness model
 * and costs one extra fetch on login.
 */
export async function clearAllLocalData(): Promise<void> {
  try {
    await db.transaction(
      'rw',
      [db.work_orders, db.molds, db.sync_queue, db.photo_stage, db.cache_meta],
      async () => {
        await db.work_orders.clear();
        await db.molds.clear();
        await db.sync_queue.clear();
        await db.photo_stage.clear();
        await db.cache_meta.clear();
      },
    );
  } catch (e) {
    if (import.meta.env.DEV) console.warn('[db] clearAllLocalData failed', e);
  }
}
